import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { UsaStates } from 'usa-states';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'components/common/Dialog';

import { reduxForm, Field, getFormValues, SubmissionError } from 'redux-form';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';
import SwitchField from 'components/fields/SwitchField';

import objectDiff from 'utils/objectDiff';
import * as validate from 'utils/validate';

import { checkPatientInsuranceEligibility } from 'redux/modules/patientsInsurance';
import {
  createPatientInsurance,
  updatePatientInsurance,
  createPatientInsuranceSpecialDate,
  updatePatientInsuranceSpecialDate,
  deletePatientInsuranceSpecialDate,
} from 'redux/modules/patientsInsurance';

import { updateInsurance } from 'redux/modules/insurances';

import dateFormat from 'config/dateFormat';
import transmissionMethods from 'config/transmissionMethods';
import insuranceRelationToInsured from 'config/insuranceRelationToInsured';
import insuranceOrderOfBenefits from 'config/insuranceOrderOfBenefits';

import InsuranceCreateEditForm from './InsuranceCreateEditForm';

const usaStates = new UsaStates();

const styles = theme => ({
  form: {
    overflow: 'hidden',
    padding: '15px 30px 30px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  action: {
    [theme.breakpoints.down('sm')]: {
      margin: '0 4px'
    },
    '&:first-child': {
      position: 'absolute',
      left: 14,
      top: 8,
      [theme.breakpoints.down('sm')]: {
        left: 0,
      },
    },
  },
  switchField: {
    position: 'absolute',
    left: 16,
    top: 16,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);

    if (values['Copay Type'] === 'Fixed' && normalizedValues['Copay Amount Fixed']) {
      normalizedValues['Copay Amount'] =
        normalizedValues['Copay Amount Fixed'].replace(/[$,]/g, '');
    }
    if (values['Copay Type'] === 'Percent' && normalizedValues['Copay Amount Percent']) {
      normalizedValues['Copay Amount'] =
        normalizedValues['Copay Amount Percent'].replace('%', '');
    }

    if (normalizedValues['Copay Type']
      && normalizedValues['Copay Type'] !== 'Fixed'
      && normalizedValues['Copay Type'] !== 'Percent') {
      normalizedValues['Copay Type'] = null;
    }

    if (normalizedValues['Relation to Insured'] === 'Self') {
      delete normalizedValues['Related First Name'];
      delete normalizedValues['Related Insurance ID'];
      delete normalizedValues['Related Date of Birth'];
      delete normalizedValues['Related Last Name'];
    }

    if (normalizedValues['Effective From']) {
      normalizedValues['Effective From'] =
        moment(normalizedValues['Effective From'], dateFormat).format(dateFormat);
    }
    if (normalizedValues['Effective From'] === '') {
      normalizedValues['Effective From'] = null;
    }
    if (normalizedValues['Effective To']) {
      normalizedValues['Effective To'] =
        moment(normalizedValues['Effective To'], dateFormat).format(dateFormat);
    }
    if (normalizedValues['Effective To'] === '') {
      normalizedValues['Effective To'] = null;
    }
    if (normalizedValues['Related Date of Birth']) {
      normalizedValues['Related Date of Birth'] =
        moment(normalizedValues['Related Date of Birth'], dateFormat).format(dateFormat);
    }

    if (normalizedValues['Insurance Status'] === true) {
      normalizedValues['Insurance Status'] = 'Active';
    } else if (normalizedValues['Insurance Status'] === false) {
      normalizedValues['Insurance Status'] = 'Inactive';
    }

    delete normalizedValues['Copay Amount Fixed'];
    delete normalizedValues['Copay Amount Percent'];
    delete normalizedValues['Insurance Info Payer Name'];
    delete normalizedValues['Payer Name'];
    delete normalizedValues['Is Available Eligibility'];
    delete normalizedValues['Claim Filling Code Name'];
    delete normalizedValues['Selected Claim Filling Code Name'];
    delete normalizedValues['Insurance Special Date'];
    delete normalizedValues['Contacts Phone'];
    delete normalizedValues['Contacts Fax'];
    delete normalizedValues['Contacts Email'];

    return normalizedValues;
  };

  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    const updatedInsuranceSpecialDate = diffValues['Insurance Special Date']
      ? values['Insurance Special Date'].filter((item) => {
        const itemInitialValue = props.initialValues['Insurance Special Date'].find(initialItem =>
          initialItem.ID === item.ID);
        if (!itemInitialValue) {
          return false;
        }
        const itemDiffValues = objectDiff(item, itemInitialValue);
        return Object.keys(itemDiffValues).length > 0;
      }).filter(item => item) : [];
    const addedInsuranceSpecialDate = diffValues['Insurance Special Date']
      ? values['Insurance Special Date'].filter(item => !item.ID)
      : [];
    const removedInsuranceSpecialDate = diffValues['Insurance Special Date']
      ? props.initialValues['Insurance Special Date']
        .filter(initialItem => values['Insurance Special Date'].filter(item => item.ID === initialItem.ID).length === 0)
      : [];
    const changedContacts = diffValues['Contacts Phone']
      || diffValues['Contacts Fax']
      || diffValues['Contacts Email']
      ? [
        ...values['Contacts Phone'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Phone',
        })),
        ...values['Contacts Fax'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Fax',
        })),
        ...values['Contacts Email'].filter(item => item.Value).map(item => ({
          Value: item.Value,
          ...item.Description ? { Description: item.Description } : {},
          Type: 'Email',
        })),
      ] : [];
    return new Promise((resolve, reject) => {
      if (updatedInsuranceSpecialDate.length) {
        dispatch(updatePatientInsuranceSpecialDate({
          values: {
            'Insurance Special Date': updatedInsuranceSpecialDate,
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
      .then(() => new Promise((resolve, reject) => {
        if (addedInsuranceSpecialDate.length) {
          dispatch(createPatientInsuranceSpecialDate({
            values: {
              'Insurance Special Date': addedInsuranceSpecialDate,
              'Insurance ID': props.ID,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedInsuranceSpecialDate.length) {
          dispatch(deletePatientInsuranceSpecialDate({
            values: {
              'Insurance Special Date': removedInsuranceSpecialDate,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (changedContacts.length) {
          dispatch(updateInsurance({
            values: {
              ID: values['Insurance Info ID'],
              'Contacts': changedContacts,
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (Object.keys(normalizeValues(diffValues)).length > 0) {
          dispatch(updatePatientInsurance({
            values: {
              ID: props.ID,
              ...normalizeValues(diffValues),
              ...diffValues['Relation to Insured']
                && ['Self', 'Employee'].indexOf(diffValues['Relation to Insured']) == -1
                ? {
                  'Related First Name': values['Related First Name'],
                  'Related Insurance ID': values['Related Insurance ID'],
                  'Related Date of Birth': values['Related Date of Birth'],
                  'Related Last Name': values['Related Last Name'],
                } : {},
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      })).then(res => res,
      (err) => {
        if (err.description) {
          throw new SubmissionError({ _error: err.description });
        }
        throw new SubmissionError(err);
      });
  }
  
  return new Promise((resolve, reject) => {
    dispatch(createPatientInsurance({
      values: {
        'User Record Number': props['User Record Number'],
        ...normalizeValues(values),
        ...values['Insurance Special Date'] && values['Insurance Special Date'].length > 0 ? {
          'Insurance Special Date': values['Insurance Special Date'],
        } : {},
      },
      resolve,
      reject,
    }));
  })
    .then(() => new Promise((resolve, reject) => {
        dispatch(updateInsurance({
          values: {
            ID: values['Insurance Info ID'],
            'Contacts': [
              ...values['Contacts Phone'].filter(item => item.Value).map(item => ({
                Value: item.Value,
                ...item.Description ? { Description: item.Description } : {},
                Type: 'Phone',
              })),
              ...values['Contacts Fax'].filter(item => item.Value).map(item => ({
                Value: item.Value,
                ...item.Description ? { Description: item.Description } : {},
                Type: 'Fax',
              })),
              ...values['Contacts Email'].filter(item => item.Value).map(item => ({
                Value: item.Value,
                ...item.Description ? { Description: item.Description } : {},
                Type: 'Email',
              }))
            ],
          },
          resolve,
          reject,
        }));
    }))
    .then(res => res,
      (err) => {
        if (err.description) {
          throw new SubmissionError({ _error: err.description });
        }
        throw new SubmissionError(err);
      });
}

function onChange(values, dispatch, props, prevValues) {
  if (values['Insurance Info ID']
    && values['Insurance Info Payer Name'] !== values['Payer Name']) {
    dispatch(props.change('Insurance Info ID', ''));
    dispatch(props.change('Is Available Eligibility', ''));
  }
  if (!props.submitting
    && values['Claim Filling Code ID']
    && values['Claim Filling Code ID'] !== prevValues['Claim Filling Code ID']) {
    if (values['Claim Filling Code ID'] === 2) {
      dispatch(props.change('Relation to Insured', 'Employee'));
      if (values['Insurance ID'] && !values['Group ID']) {
        dispatch(props.change('Group ID', values['Insurance ID']));
      }
    }
    if (values['Claim Filling Code ID'] === 3) {
      dispatch(props.change('Relation to Insured', 'Self'));
      if (values['Insurance ID'] && !values['Group ID']) {
        dispatch(props.change('Group ID', values['Insurance ID']));
      }
    }
  }
  if (values['Claim Filling Code ID']
    && values['Selected Claim Filling Code Name'] !== values['Claim Filling Code Name']) {
    dispatch(props.change('Claim Filling Code ID', null));
  }
}

const validateForm = (values) => {
  const errors = {};

  if (!values['Insurance Info ID'] || values['Insurance Info Payer Name'] !== values['Payer Name']) {
    errors['Payer Name'] = 'Please, select insurance from list';
  }

  if (values['Transmission Method'] && transmissionMethods.indexOf(values['Transmission Method']) === -1) {
    errors['Transmission Method'] = 'Please, select Transmission Method from list';
  }

  if (!values['Copay Amount Fixed'] && values['Copay Type'] === 'Fixed') {
    errors['Copay Amount Fixed'] = 'Field is required';
  }

  if (!values['Copay Amount Percent'] && values['Copay Type'] === 'Percent') {
    errors['Copay Amount Percent'] = 'Field is required';
  }

  if (!values['Group ID'] && values['Claim Filling Code ID']
    && (values['Claim Filling Code ID'] === 2 || values['Claim Filling Code ID'] === 3)) {
    errors['Group ID'] = 'Field is required';
  }

  if (values['Claim Filling Code ID'] && values['Claim Filling Code ID'] === 3 
    && (!values['Auto Accident State'] || usaStates.arrayOf('abbreviations').indexOf(values['Auto Accident State']) === -1)) {
    errors['Auto Accident State'] = 'Please, select valid state abbreviation';
  }

  if (values['Effective To']
    && !validate.date(values['Effective To'])
    && values['Effective From']
    && !validate.date(values['Effective From'])
    && moment(values['Effective To'], dateFormat).isBefore(moment(values['Effective From'], dateFormat))) {
    errors['Effective To'] = 'Effective To should be after Effective From';
  }

  if (values['Relation to Insured'] && insuranceRelationToInsured.indexOf(values['Relation to Insured']) === -1) {
    errors['Relation to Insured'] = 'Please, select Relation to Insured from list';
  }

  if (values['Order Benefits'] && insuranceOrderOfBenefits.indexOf(values['Order Benefits']) === -1) {
    errors['Order Benefits'] = 'Please, select Order of Benefits from list';
  }

  if (!values['Claim Filling Code ID']
    || values['Selected Claim Filling Code Name'] !== values['Claim Filling Code Name']) {
    errors['Claim Filling Code Name'] = 'Please, select Payer Type from list';
  }

  if (values['Insurance Status'] === false && !values['Effective To']) {
    errors['Effective To'] = 'End Date is required for Inactive Insurance';
  }

  if (values['Contacts Phone'] && values['Contacts Phone'].length > 0){
    errors['Contacts Phone'] = values['Contacts Phone'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Phone'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Phone'];
    }
  }

  if (values['Contacts Fax'] && values['Contacts Fax'].length > 0){
    errors['Contacts Fax'] = values['Contacts Fax'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Fax'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Fax'];
    }
  }

  if (values['Contacts Email'] && values['Contacts Email'].length > 0){
    errors['Contacts Email'] = values['Contacts Email'].map(item => {
      if (!item.Value && item.Description) {
        return {
          Value: 'Please, enter value for description',
        };
      }
      return undefined;
    });
    if (errors['Contacts Email'].filter(item => item !== undefined).length === 0) {
      delete errors['Contacts Email'];
    }
  }

  return errors;
};

const warnForm = (values) => {
  const warnings = {};
  const mainWarning = [];

  if (values['Is Available Eligibility'] !== undefined
    && !values['Is Available Eligibility']) {
    // eslint-disable-next-line no-underscore-dangle
    mainWarning.push('Sorry, but it is not possible to check eligibility for this Insurance Company.');
  }

  if (values['Claim Filling Code ID']
    && values['Claim Filling Code ID'] === 3
    && (!values['Insurance Special Date'] 
     || (values['Insurance Special Date'] && !values['Insurance Special Date'].find(item => item['Type Of Date'] === 'Accident Date')))) {
    mainWarning.push('It will not be possible to submit claim for this Automobile Medical Insurance without Accident Date entered.');
  }

  if (mainWarning.length > 0) {
    warnings._warning = mainWarning;
  }

  return warnings;
};

class InsuranceCreateEditDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      autosuggestFieldFocused: false,
    };

    this.onAutosuggestFieldFocus = this.onAutosuggestFieldFocus.bind(this);
    this.onAutosuggestFieldBlur = this.onAutosuggestFieldBlur.bind(this);
  }

  onAutosuggestFieldFocus() {
    this.setState({ autosuggestFieldFocused: true });
  }

  onAutosuggestFieldBlur() {
    this.setState({ autosuggestFieldFocused: false });
  }

  render() {
    const {
      open,
      onClose,
      onSubmitSuccess,
      form,
      ID,
      initialValues,
      actions,
      submitting,
      dirty,
      formValues,
      submit,
      reset,
      appInfo,
      classes,
      change,
      error,
      warning,
      ...otherProps
    } = this.props;
    const { autosuggestFieldFocused } = this.state;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
      >
        <DialogTitle
          onCloseClick={onClose}
          secondaryContent={(
            <Field
              className={classes.switchField}
              id="Insurance Status"
              name="Insurance Status"
              label="Active"
              component={SwitchField}
            />
          )}
        >
          <Typography
            variant="title"
            color="secondary"
            align="center"
          >
            {ID ? 'Edit Insurance' : 'Add Insurance'}
          </Typography>
        </DialogTitle>
        <DialogContent
          withScrollbars
          disableMouseScroll={autosuggestFieldFocused}
        >
          <InsuranceCreateEditForm
            className={classes.form}
            {...{ 'User Record Number': otherProps['User Record Number'] }}
            {...{ 'Is Available Eligibility': otherProps['Is Available Eligibility'] }}
            form={form}
            onAutosuggestFieldFocus={this.onAutosuggestFieldFocus}
            onAutosuggestFieldBlur={this.onAutosuggestFieldBlur}
            change={change}
            error={error}
            warning={warning}
          />
        </DialogContent>
        <DialogActions
          {...otherProps['Is Available Eligibility'] ? { classes: {
            action: classes.action,
          } } : {}}
        >
          {otherProps['Is Available Eligibility'] &&
            <div>
              <Button
                onClick={() => {
                  actions.checkPatientInsuranceEligibility({
                    'Patient ID': otherProps['User Record Number'],
                    'Insurance ID': ID,
                  });
                }}
                disabled={!ID || dirty || otherProps['Checking Eligibility'] || ['demo', 'clients'].indexOf(appInfo['APP NAME']) !== -1}
                color="primary"
                underline
              >
                Check Eligibility
              </Button>
              {otherProps['Checking Eligibility'] &&
                <Progress />
              }
            </div>
          }
          <Button
            onClick={() => reset()}
            variant="raised"
          >
            Reset
          </Button>
          <Button
            onClick={() => {
              if (ID) {
                if (Object.keys(objectDiff(initialValues, formValues)).length > 0) {
                  submit();
                } else {
                  onClose();
                }
              } else if (dirty) {
                submit();
              } else {
                onClose();
              }
            }}
            variant="raised"
            color="primary"
            disabled={submitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

InsuranceCreateEditDialog.propTypes = {
  open: PropTypes.bool,
  ID: PropTypes.number,
  'User Record Number': PropTypes.number,
  initialValues: PropTypes.shape({
    'Insurance Status': PropTypes.bool,
  }),
  form: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  submitting: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  formValues: PropTypes.shape({
    'Insurance Status': PropTypes.bool,
  }),
  actions: PropTypes.shape({
    checkPatientInsuranceEligibility: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    form: PropTypes.string.isRequired,
    action: PropTypes.string.isRequired,
  }).isRequired,
};

InsuranceCreateEditDialog.defaultProps = {
  open: false,
  ID: null,
  'User Record Number': null,
  initialValues: null,
  onClose: () => {},
  onSubmitSuccess: () => {},
  formValues: null,
};

const mapStateToProps = (store, ownProps) => ({
  formValues: getFormValues(ownProps.form)(store),
  appInfo: store.appInfo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ checkPatientInsuranceEligibility }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({
  onChange,
  validate: validateForm,
  warn: warnForm,
  onSubmit,
  enableReinitialize: true,
})(
  withStyles(styles)(InsuranceCreateEditDialog),
));



// WEBPACK FOOTER //
// ./src/components/patient/InsuranceCreateEditDialog/InsuranceCreateEditDialog.js